import { Button, DataTable, Form, Modal, ResultsCard, ResultsToolbar, RowActions } from 'shared/components/ui';
import React, { useState, useEffect } from 'react';
import { AttachMoney, Add, Save, Search, Clear } from '@mui/icons-material';

import Header from '../../../shared/components/layout/Header';
import Footer from '../../../shared/components/layout/Footer';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Toast } from '../../../shared/components/Toast';
import { usePagoPorEstudio } from '../hooks/usePagoPorEstudio';
import '../../../features/turnos/components/GrillaTurnos.css';
import '../../../styles/buttons.css';

const FORM_INICIAL = { idTipoEstudio: '', idCentro: '', importe1: '', importe2: '', importe3: '', esTecnico: true };

const GrillaPagoPorEstudio = () => {
  const { pagos, tiposEstudios, centros, roles, loading, cargarFiltros, buscar, guardar, actualizar, eliminar } = usePagoPorEstudio();
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroRol, setFiltroRol] = useState('2'); // 2 = Técnico por defecto
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    cargarFiltros();
  }, [cargarFiltros]);

  useEffect(() => {
    buscar(filtroTipo || null, filtroRol === '2');
  }, []);  // eslint-disable-line

  const handleBuscar = () => buscar(filtroTipo || null, filtroRol === '2');
  const handleLimpiar = () => { setFiltroTipo(''); buscar(null, true); };

  const abrirNuevo = () => { setForm({ ...FORM_INICIAL, esTecnico: filtroRol === '2' }); setModal({ mode: 'nuevo' }); };
  const abrirEditar = (item) => {
    setForm({
      idTipoEstudio: item.idTipoEstudio ?? '',
      idCentro: item.idCentro ?? '',
      importe1: item.importe1 ?? '',
      importe2: item.importe2 ?? '',
      importe3: item.importe3 ?? '',
      esTecnico: item.esTecnico,
    });
    setModal({ mode: 'editar', item });
  };

  const esDuplicado = (idTipoEstudio, idCentro, esTecnico, excludeId) =>
    pagos.some(p =>
      p.idTipoEstudio === parseInt(idTipoEstudio) &&
      p.idCentro === parseInt(idCentro) &&
      p.esTecnico === esTecnico &&
      p.idPagoTipoEstudio !== excludeId
    );

  const handleGuardar = async (e) => {
    e.preventDefault();
    const body = {
      IdTipoEstudio: parseInt(form.idTipoEstudio),
      IdCentro: parseInt(form.idCentro),
      Importe1: form.importe1 === '' ? null : parseFloat(form.importe1),
      Importe2: form.importe2 === '' ? null : parseFloat(form.importe2),
      Importe3: form.importe3 === '' ? null : parseFloat(form.importe3),
      EsTecnico: form.esTecnico,
    };
    const excludeId = modal.mode === 'editar' ? modal.item.idPagoTipoEstudio : undefined;
    if (esDuplicado(form.idTipoEstudio, form.idCentro, form.esTecnico, excludeId)) {
      setToast({ message: 'Ya existe un registro para esta combinación de práctica, centro y rol.', type: 'error' });
      return;
    }
    try {
      if (modal.mode === 'editar') {
        await actualizar(modal.item.idPagoTipoEstudio, body);
        setToast({ message: 'Importe actualizado con éxito.', type: 'success' });
      } else {
        await guardar(body);
        setToast({ message: 'Importe guardado con éxito.', type: 'success' });
      }
      setModal(null);
      buscar(filtroTipo || null, filtroRol === '2');
    } catch {
      setToast({ message: 'Error al guardar.', type: 'error' });
    }
  };

  const confirmarEliminar = async () => {
    try {
      await eliminar(confirmEliminar.id);
      setToast({ message: 'Importe eliminado con éxito.', type: 'success' });
      buscar(filtroTipo || null, filtroRol === '2');
    } catch {
      setToast({ message: 'Error al eliminar.', type: 'error' });
    } finally {
      setConfirmEliminar(null);
    }
  };

  return (
    <div className="grilla-turnos">
      <Header />
      <div className="main-content">
        <div className="page-header">
          <h2 className="page-title-grilla">
            <AttachMoney sx={{ fontSize: 32, mr: 1, verticalAlign: 'middle', color: '#6d1212' }} />
            Importe por Práctica
          </h2>
          <span className="page-subtitle">Gestión de importes por tipo de práctica y centro</span>
        </div>

        <Modal show={modal !== null} onHide={() => setModal(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{modal?.mode === 'editar' ? 'Editar Importe' : 'Nuevo Importe'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleGuardar}>
            <Modal.Body style={{ padding: '1.75rem' }}>
              <div className="row g-3">
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Tipo de Práctica *</Form.Label>
                    <Form.Select value={form.idTipoEstudio} onChange={(e) => setForm(f => ({ ...f, idTipoEstudio: e.target.value }))} required>
                      <option value="">Seleccione...</option>
                      {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label>Centro *</Form.Label>
                    <Form.Select
                      value={form.idCentro}
                      onChange={(e) => setForm(f => ({ ...f, idCentro: e.target.value }))}
                      required
                      isInvalid={!!(form.idTipoEstudio && form.idCentro && esDuplicado(form.idTipoEstudio, form.idCentro, form.esTecnico, modal?.mode === 'editar' ? modal.item.idPagoTipoEstudio : undefined))}
                    >
                      <option value="">Seleccione...</option>
                      {centros.map(c => <option key={c.idCentro} value={c.idCentro}>{c.centro1 || c.centro}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Ya existe un registro para esta combinación de práctica, centro y rol.
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Importe 1</Form.Label>
                    <Form.Control type="number" step="0.01" min="0" value={form.importe1} onChange={(e) => setForm(f => ({ ...f, importe1: e.target.value }))} placeholder="0.00" />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Importe 2</Form.Label>
                    <Form.Control type="number" step="0.01" min="0" value={form.importe2} onChange={(e) => setForm(f => ({ ...f, importe2: e.target.value }))} placeholder="0.00" />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Importe 3</Form.Label>
                    <Form.Control type="number" step="0.01" min="0" value={form.importe3} onChange={(e) => setForm(f => ({ ...f, importe3: e.target.value }))} placeholder="0.00" />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Rol *</Form.Label>
                    <div style={{ display: 'flex', gap: '0.5rem', paddingTop: '0.25rem' }}>
                      {[{ label: 'Técnico', value: true }, { label: 'Médico', value: false }].map(r => (
                        <Button
                          key={String(r.value)}
                          type="button"
                          size="sm"
                          variant={form.esTecnico === r.value ? 'primary' : 'outline-secondary'}
                          onClick={() => setForm(f => ({ ...f, esTecnico: r.value }))}
                          style={{ minWidth: '80px' }}
                        >
                          {r.label}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setModal(null)} disabled={loading}>Cancelar</Button>
              <Button type="submit" variant="success" disabled={loading || !form.idTipoEstudio || !form.idCentro || esDuplicado(form.idTipoEstudio, form.idCentro, form.esTecnico, modal?.mode === 'editar' ? modal.item.idPagoTipoEstudio : undefined)}>
                <Save sx={{ fontSize: 16, mr: 0.5 }} /> {modal?.mode === 'editar' ? 'Actualizar' : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <div className="filters-card">
          <div className="filters-header"><h3 className="filters-title">Filtros de Búsqueda</h3></div>
          <div className="filters-content">
            <div className="filters-row">
              <div className={`filter-group ${filtroTipo ? 'filter-active' : ''}`}>
                <label className="filter-label">Tipo de Práctica</label>
                <select className="filter-input" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
                  <option value="">Todos</option>
                  {tiposEstudios.map(t => <option key={t.idTipoEstudio} value={t.idTipoEstudio}>{t.tipoEstudio}</option>)}
                </select>
              </div>
              <div className={`filter-group ${filtroRol ? 'filter-active' : ''}`}>
                <label className="filter-label">Rol</label>
                <select className="filter-input" value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)}>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.rol}</option>)}
                </select>
              </div>
              <div className="filter-actions">
                <Button className="btn-search" onClick={handleBuscar} disabled={loading}>
                  <Search sx={{ fontSize: 18, mr: 0.5 }} /> Buscar
                </Button>
                <Button className="btn-clear" onClick={handleLimpiar}>
                  <Clear sx={{ fontSize: 18, mr: 0.5 }} /> Limpiar
                </Button>
              </div>
            </div>
          </div>
        </div>

        <ResultsCard className="results-card">
          <ResultsToolbar className="results-header">
            <div className="results-header-left"><h3 className="results-title">Resultados</h3></div>
            <div className="results-info">
              <span className="results-count">{pagos.length} registros</span>
              {loading && <span className="loading-indicator">Cargando...</span>}
              <Button variant="success" size="sm" onClick={abrirNuevo} className="btn-new-turno">
                <Add sx={{ fontSize: 18, mr: 0.5 }} /> Nuevo
              </Button>
            </div>
          </ResultsToolbar>
          <div className="table-container table-container-scrollable">
            <DataTable className="data-table">
              <thead>
                <tr>
                  <th>Tipo de Práctica</th>
                  <th>Centro</th>
                  <th style={{ textAlign: 'center' }}>Importe 1</th>
                  <th style={{ textAlign: 'center' }}>Importe 2</th>
                  <th style={{ textAlign: 'center' }}>Importe 3</th>
                  <th style={{ textAlign: 'center' }}>Rol</th>
                  <th className="action-cell">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pagos.length === 0 ? (
                  <tr><td colSpan={7} className="empty-state">{loading ? 'Cargando...' : 'No hay registros disponibles'}</td></tr>
                ) : pagos.map((p) => (
                  <tr key={p.idPagoTipoEstudio}>
                    <td>{p.tipoEstudio}</td>
                    <td>{p.centro}</td>
                    <td style={{ textAlign: 'center' }}>{p.importe1 != null ? `$${Number(p.importe1).toFixed(2)}` : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{p.importe2 != null ? `$${Number(p.importe2).toFixed(2)}` : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{p.importe3 != null ? `$${Number(p.importe3).toFixed(2)}` : '-'}</td>
                    <td style={{ textAlign: 'center' }}>{p.esTecnico ? 'Técnico' : 'Médico'}</td>
                    <td className="action-cell">
                      <RowActions onEdit={() => abrirEditar(p)} onDelete={() => setConfirmEliminar({ id: p.idPagoTipoEstudio, label: p.tipoEstudio })} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        </ResultsCard>
      </div>
      <Footer />

      <ConfirmModal
        show={confirmEliminar !== null}
        title="Eliminar Importe"
        message={`¿Eliminar el importe de "${confirmEliminar?.label}"?`}
        onConfirm={confirmarEliminar}
        onCancel={() => setConfirmEliminar(null)}
      />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default GrillaPagoPorEstudio;
